import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaCheckCircle,
  FaChevronDown,
  FaClipboardList,
  FaCog,
  FaFileCsv,
  FaSearch,
  FaStoreAlt,
  FaTimesCircle,
  FaTrashAlt,
  FaUser,
  FaUserSlash,
} from 'react-icons/fa';
import { httpAdmin } from '../lib/http';
import { useDebouncedValue, useGuard401, PaginationBar } from './adminShared';
import './AdminActivity.css';

const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'users', label: 'Users' },
  { id: 'providers', label: 'Providers' },
  { id: 'business', label: 'Business' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'system', label: 'System' },
];

const PAGE_SIZE = 25;

function pickArray(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

function pickTotal(res, items) {
  if (typeof res?.total === 'number') return res.total;
  if (typeof res?.count === 'number') return res.count;
  return items.length;
}

function formatRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.round((d.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSec / (86400 * 30)), 'month');
  return rtf.format(Math.round(diffSec / (86400 * 365)), 'year');
}

function formatAction(action) {
  return String(action || 'unknown').replace(/_/g, ' ').toLowerCase();
}

function actionTone(action) {
  const a = String(action || '').toUpperCase();
  if (/DELETED|REJECTED|BLOCK|CANCELLED|FLAGGED/.test(a)) return 'danger';
  if (/APPROVED|CREATED|VERIFIED|PROCESSED|EXTENDED/.test(a)) return 'ok';
  if (/SETTING_|SYSTEM_|ANALYTICS_/.test(a)) return 'muted';
  return 'default';
}

function ActionIcon({ action }) {
  const a = String(action || '').toUpperCase();
  if (/DELETE_ACCOUNT_/.test(a)) return <FaUserSlash aria-hidden="true" />;
  if (/DELETED|TRASH/.test(a)) return <FaTrashAlt aria-hidden="true" />;
  if (/REJECTED|FLAGGED|BLOCK/.test(a)) return <FaTimesCircle aria-hidden="true" />;
  if (/APPROVED|VERIFIED|PROCESSED|EXTENDED|CREATED/.test(a)) return <FaCheckCircle aria-hidden="true" />;
  if (/PROVIDER_|BUSINESS_|ANALYTICS_/.test(a)) return <FaStoreAlt aria-hidden="true" />;
  if (/USER_/.test(a)) return <FaUser aria-hidden="true" />;
  if (/SETTING_|SYSTEM_/.test(a)) return <FaCog aria-hidden="true" />;
  if (/REVIEW_/.test(a)) return <FaCheckCircle aria-hidden="true" />;
  return <FaClipboardList aria-hidden="true" />;
}

function downloadCsv(filename, rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

export default function AdminActivity() {
  const guard = useGuard401();
  const nav = useNavigate();
  const location = useLocation();

  const initialSearch = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get('search') || '';
    } catch {
      return '';
    }
  }, [location.search]);

  const [activities, setActivities] = useState([]);
  const [total, setTot] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [liveRefresh, setLiveRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (initialSearch !== search) setSearch(initialSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);

  useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch, dateFrom, dateTo]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('filter', filter);
    params.set('search', debouncedSearch || '');
    params.set('page', String(page));
    params.set('limit', String(PAGE_SIZE));
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    return params.toString();
  }, [filter, debouncedSearch, page, dateFrom, dateTo]);

  const loadActivities = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setLoadError('');
    try {
      const res = await httpAdmin.get(`/api/admin/activity?${queryString}`);
      const items = pickArray(res);
      setActivities(items);
      setTot(pickTotal(res, items));
      setLastUpdated(new Date());
    } catch (e) {
      if (!guard(e)) setLoadError(e?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [queryString, guard]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    if (!liveRefresh) return undefined;
    const id = setInterval(() => loadActivities({ soft: true }), 15000);
    return () => clearInterval(id);
  }, [liveRefresh, loadActivities]);

  const hasActiveFilters = Boolean(
    filter !== 'all' || debouncedSearch || dateFrom || dateTo,
  );

  const clearFilters = () => {
    setFilter('all');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set('filter', filter);
      params.set('search', debouncedSearch || '');
      params.set('page', '1');
      params.set('limit', '5000');
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await httpAdmin.get(`/api/admin/activity?${params.toString()}`);
      const items = pickArray(res);
      const rows = items.map((a) => ({
        time: a.createdAt ? new Date(a.createdAt).toISOString() : '',
        actorName: a.actor?.name || '',
        actorEmail: a.actor?.email || '',
        action: a.action || '',
        targetType: a.targetType || '',
        targetId: a.targetId || '',
        metadata: a.metadata ? JSON.stringify(a.metadata) : '',
      }));
      downloadCsv(`booksy-activity-${Date.now()}.csv`, rows);
    } catch (e) {
      if (!guard(e)) setLoadError(e?.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const openActor = (actor) => {
    if (!actor) return;
    const q = actor.email || actor.name || '';
    const role = String(actor.role || '').toLowerCase();
    if (role.includes('shop') || role.includes('owner') || role.includes('provider') || role.includes('business')) {
      nav(`/admin/providers${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    } else {
      nav(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
    }
  };

  const openTarget = (activity) => {
    const type = String(activity.targetType || '').toLowerCase();
    const id = activity.targetId || '';
    if (type === 'user') {
      nav(`/admin/users${id ? `?q=${encodeURIComponent(id)}` : ''}`);
    } else if (type === 'business') {
      nav(`/admin/providers${id ? `?q=${encodeURIComponent(id)}` : ''}`);
    } else if (type === 'review') {
      nav('/admin/reviews');
    } else if (type.includes('delete')) {
      nav('/admin/delete-requests');
    }
  };

  return (
    <section className="adm-page adm-page--activity">
      <header className="adm-page-head adm-act-head">
        <div>
          <h2>Activity Feed ({total})</h2>
          <p className="adm-act-desc">
            Audit trail for admin actions across users, partners, reviews, and settings.
          </p>
        </div>
      </header>

      <div className="adm-users-tools adm-act-tools">
        <div className="adm-users-tools__row adm-users-tools__row--search">
          <form
            className="adm-search-group"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              loadActivities();
            }}
          >
            <input
              type="text"
              placeholder="Search action, target, actor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="adm-search"
              aria-label="Search activities"
            />
            <button type="submit" className="adm-search-btn" aria-label="Search">
              <FaSearch aria-hidden="true" />
              <span>Search</span>
            </button>
          </form>
        </div>

        <div className="adm-users-tools__row adm-users-tools__row--filters">
          <div className="adm-filter-chips" role="group" aria-label="Filter by type">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`adm-filter-chip${filter === f.id ? ' is-active' : ''}`}
                onClick={() => setFilter(f.id)}
                aria-pressed={filter === f.id}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="adm-users-tools__actions">
            <label className="adm-act-live">
              <input
                type="checkbox"
                checked={liveRefresh}
                onChange={(e) => setLiveRefresh(e.target.checked)}
              />
              Live refresh
            </label>
            <button
              type="button"
              className="adm-export-btn"
              onClick={exportCsv}
              disabled={exporting || loading}
            >
              <FaFileCsv aria-hidden="true" />
              <span>{exporting ? 'Exporting…' : 'Export CSV'}</span>
            </button>
          </div>
        </div>

        <div className="adm-act-dates" role="group" aria-label="Filter by date range">
          <label>
            From
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="Activity date from"
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="Activity date to"
            />
          </label>
          {lastUpdated && (
            <span className="adm-act-updated">
              Updated {lastUpdated.toLocaleTimeString()}
              {liveRefresh ? ' · auto 15s' : ''}
            </span>
          )}
        </div>
      </div>

      {loadError && (
        <div className="adm-error" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn-sm btn-secondary" onClick={() => loadActivities()}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="adm-act-loading" aria-busy="true" aria-label="Loading activity">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="adm-act-skel" />
          ))}
        </div>
      ) : activities.length === 0 && !loadError ? (
        <div className="adm-entity-empty adm-entity-empty--activity">
          <p className="adm-entity-empty__title">No activities found</p>
          <p className="adm-entity-empty__desc">
            {hasActiveFilters
              ? 'Nothing matches the current type, search, or date range.'
              : 'Audit events will appear here as admins take actions.'}
          </p>
          {hasActiveFilters && (
            <button type="button" className="btn-secondary" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      ) : activities.length === 0 ? null : (
        <div className="adm-act-feed" aria-label="Activity timeline">
          {activities.map((activity, index) => {
            const id = activity._id || String(index);
            const open = expandedId === id;
            const tone = actionTone(activity.action);
            const actorLabel = activity.actor?.name || activity.actor?.email || 'System';
            const absolute = activity.createdAt
              ? new Date(activity.createdAt).toLocaleString()
              : '';
            const metaEntries = activity.metadata && typeof activity.metadata === 'object'
              ? Object.entries(activity.metadata)
              : [];

            return (
              <article
                key={id}
                className={`adm-entity-card adm-act-card${open ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="adm-entity-row adm-act-row"
                  onClick={() => setExpandedId(open ? null : id)}
                  aria-expanded={open}
                >
                  <span className="adm-act-rail" aria-hidden="true" />
                  <span className={`adm-act-icon adm-act-icon--${tone}`}>
                    <ActionIcon action={activity.action} />
                  </span>
                  <div className="adm-act-body">
                    <div className="adm-act-top">
                      <strong className="adm-act-action">{formatAction(activity.action)}</strong>
                      <time className="adm-act-time" dateTime={activity.createdAt || undefined} title={absolute}>
                        {formatRelative(activity.createdAt)}
                      </time>
                    </div>
                    <div className="adm-act-meta-line">
                      {activity.actor ? (
                        <button
                          type="button"
                          className="adm-act-actor"
                          onClick={(e) => {
                            e.stopPropagation();
                            openActor(activity.actor);
                          }}
                        >
                          {actorLabel}
                        </button>
                      ) : (
                        <span className="adm-act-actor is-system">System</span>
                      )}
                      {activity.targetType && (
                        <span className="adm-act-pill">{activity.targetType}</span>
                      )}
                    </div>
                  </div>
                  <FaChevronDown
                    className={`adm-entity-chevron${open ? ' is-open' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {open && (
                  <div className="adm-act-panel">
                    <div className="adm-act-panel__grid">
                      <div>
                        <h4>When</h4>
                        <p>{absolute || '—'}</p>
                      </div>
                      <div>
                        <h4>Actor</h4>
                        <p>
                          {activity.actor ? (
                            <button type="button" className="adm-act-link" onClick={() => openActor(activity.actor)}>
                              {actorLabel}
                              {activity.actor.email ? ` · ${activity.actor.email}` : ''}
                            </button>
                          ) : 'System'}
                        </p>
                      </div>
                      <div>
                        <h4>Target</h4>
                        <p>
                          {activity.targetType || '—'}
                          {activity.targetId ? (
                            <>
                              {' · '}
                              <button
                                type="button"
                                className="adm-act-link"
                                onClick={() => openTarget(activity)}
                              >
                                {activity.targetId}
                              </button>
                            </>
                          ) : null}
                        </p>
                      </div>
                      <div>
                        <h4>Action</h4>
                        <p><code>{activity.action || '—'}</code></p>
                      </div>
                    </div>

                    <div className="adm-act-panel__block">
                      <h4>Metadata</h4>
                      {metaEntries.length ? (
                        <dl className="adm-act-kv">
                          {metaEntries.map(([k, v]) => (
                            <div key={k}>
                              <dt>{k}</dt>
                              <dd>{typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v)}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : (
                        <p className="adm-act-muted">No metadata on this event.</p>
                      )}
                    </div>

                    <div className="adm-act-panel__block">
                      <h4>Raw</h4>
                      <pre className="adm-act-raw">{JSON.stringify({
                        _id: activity._id,
                        action: activity.action,
                        targetType: activity.targetType,
                        targetId: activity.targetId,
                        metadata: activity.metadata || null,
                        createdAt: activity.createdAt,
                        actor: activity.actor
                          ? { _id: activity.actor._id, name: activity.actor.name, email: activity.actor.email, role: activity.actor.role }
                          : null,
                      }, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {!loading && !loadError && activities.length > 0 && (
        <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      )}
    </section>
  );
}
